import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ColorBrandBlue1, Spacing16 } from 'autogen/design-tokens/tokens';
import type { FooterMenuViewModel } from 'autogen/swagger/all';
import { Body } from 'components/generic/typography';
import debounce from 'debounce';
import matchMedia from 'modules/helpers/matchMedia';
import { stringToKey } from 'modules/helpers/strings';
import { rem } from 'modules/helpers/style';
import {
    Accordion,
    AccordionItem,
    AccordionItemArrow,
    AccordionItemButton,
    AccordionItemHeading,
    AccordionItemPanel
} from 'modules/react-accordion';
import { breakpoint, colors, sizes } from 'variables';
import NavigationItems from './NavgiationItems';

const Wrapper = styled.div`
    display: block;
    background-color: ${ColorBrandBlue1};

    ${breakpoint.up(sizes.lg)} {
        padding-top: ${rem(40)};
    }
`;

const Container = styled.div`
    padding: 0;

    ${breakpoint.up(sizes.lg)} {
        padding-left: ${Spacing16};
        padding-right: ${Spacing16};
    }
`;

const StyledAccordion = styled(Accordion)`
    border: none;

    ${breakpoint.up(sizes.lg)} {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        column-gap: 20px;
        row-gap: 40px;
        padding-bottom: ${rem(40)};
    }

    ${breakpoint.up(sizes.xl)} {
        column-gap: 40px;
    }

    ${breakpoint.up(sizes.xxl)} {
        column-gap: 96px;
    }

    ${breakpoint.up(sizes.xxxl)} {
        column-gap: 190px;
    }
`;

const StyledAccordionItem = styled(AccordionItem)`
    border-top: none;

    &.is-accordion {
        border-top: 1px solid ${colors.footerAccordion};

        & + .is-accordion {
            border-top: none; // cancel border on following elements
        }
    }

    &.last-static {
        padding-bottom: ${rem(35)};
    }

    ${breakpoint.up(sizes.lg)} {
        &.is-accordion {
            border-top: none;
        }

        &.last-static {
            padding-bottom: 0;
        }
    }
`;

const StyledAccordionItemHeading = styled(AccordionItemHeading)<{ isStaticHeading: boolean }>`
    ${(props) => !props.isStaticHeading && `border-bottom: 1px solid ${colors.footerAccordion}`};

    ${breakpoint.up(sizes.lg)} {
        border-bottom: none;
    }
`;

const StyledAccordionItemPanel = styled(AccordionItemPanel)<{ isStaticPanel: boolean }>`
    background-color: ${(props) => (props.isStaticPanel ? colors.brand1 : colors.footerAccordion)};
    padding: ${(props) => (props.isStaticPanel ? '0 25px' : `${Spacing16} 25px 25px`)};

    ${breakpoint.up(sizes.lg)} {
        background-color: transparent;
        padding: 0;
        margin-top: 10px;
        line-height: ${rem(26)};
    }
`;

const StyledAccordionItemArrow = styled(AccordionItemArrow)`
    width: 12px;
    height: 12px;
    fill: ${colors.negative};

    ${breakpoint.up(sizes.lg)} {
        display: none;
    }
`;

const StyledAccordionItemButton = styled(AccordionItemButton)<{ hasClickHandler: boolean }>`
    display: flex;
    align-items: center;
    text-align: left;
    padding: ${(props) => (props.hasClickHandler ? `${Spacing16} 25px` : '25px 25px 0px')};

    ${breakpoint.up(sizes.lg)} {
        font-weight: bold;
        padding: 0;
        pointer-events: none;
    }

    ${(props) =>
        !props.hasClickHandler &&
        `
        pointer-events: none;

        & ${StyledAccordionItemArrow} {
            display: none;
        }
    `}
`;

const StyledBody = styled(Body)`
    flex: 1;
    color: ${colors.negative};
`;

export type NavigationFooterType = {
    menus: FooterMenuViewModel[];
};

export default function NavigationFooter(props: NavigationFooterType): JSX.Element {
    const { menus } = props;

    const [isTablet, setIsTablet] = useState<boolean>(matchMedia.isTablet);
    const [expandedItems, setExpandedItems] = useState<any>(menus.map((menu) => menu.title));

    const handleResize = debounce((): void => setIsTablet(matchMedia.isTablet), 100);

    const handleExpandedItems = useCallback(
        (tablet: boolean): void => {
            if (tablet) {
                setExpandedItems(menus.map((menu) => menu.title));
            } else {
                setExpandedItems(menus.filter((menu) => !menu.isMobileAccordion).map((menu) => menu.title));
            }
        },
        [menus]
    );

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return (): void => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    useEffect(() => {
        handleExpandedItems(isTablet);
    }, [handleExpandedItems, isTablet]);

    return (
        <Wrapper>
            <Container className="container-fluid">
                <StyledAccordion allowMultipleExpanded expandedItems={expandedItems}>
                    {menus.map((menu, index, array) => {
                        const { title, isMobileAccordion, menuItems } = menu;
                        const nextItem = array[index + 1];
                        const lastStatic = !nextItem || nextItem.isMobileAccordion;

                        return (
                            <StyledAccordionItem
                                key={stringToKey(title)}
                                uuid={title}
                                className={
                                    (isMobileAccordion ? 'is-accordion' : '') || (lastStatic ? 'last-static' : '')
                                }
                            >
                                <StyledAccordionItemHeading isStaticHeading={!isMobileAccordion}>
                                    <StyledAccordionItemButton hasClickHandler={isMobileAccordion}>
                                        <StyledBody forwardedAs="span" size="L" strong>
                                            {title}
                                        </StyledBody>
                                        <StyledAccordionItemArrow />
                                    </StyledAccordionItemButton>
                                </StyledAccordionItemHeading>
                                <StyledAccordionItemPanel isStaticPanel={!isMobileAccordion}>
                                    <NavigationItems items={menuItems} />
                                </StyledAccordionItemPanel>
                            </StyledAccordionItem>
                        );
                    })}
                </StyledAccordion>
            </Container>
        </Wrapper>
    );
}
